import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import {
  MapPin,
  ShoppingBag,
  CheckCircle,
  MessageCircle,
  Send,
  Star,
  ArrowRight,
  Phone,
  Mail,
  Instagram,
  Clock,
  Shield,
  Eye,
  Zap,
  Users,
  Package,
  Compass,
  Search,
  ChevronLeft,
  ChevronRight,
  Pause,
  Play,
  Menu,
  X,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { buildWhatsAppUrl, sanitizeHtml } from "@/lib/content";
import BrandLogo from "@/components/BrandLogo";
import WhatsAppIcon from "@/components/WhatsAppIcon";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Carousel, CarouselContent, CarouselItem } from "@/components/ui/carousel";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const ELEMENTOR_SHOPPER_FORM_URL = process.env.REACT_APP_ELEMENTOR_SHOPPER_FORM_URL;
const MAILPOET_NEWSLETTER_FORM_URL = process.env.REACT_APP_MAILPOET_NEWSLETTER_FORM_URL;

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 },
  },
};

const DEFAULT_MARKETS = [
  {
    name: "EKO Market",
    description:
      "Balogun, Idumota, Èbúté Èrò, and other Lagos Island market clusters for fashion, fabrics, provisions, and household sourcing.",
    image:
      "https://images.unsplash.com/photo-1768212565426-58b089b6386d?auto=format&fit=crop&q=80&w=800",
    large: true,
  },
  {
    name: "TRADE FAIR Market",
    description:
      "Wholesale destination for fashion, imported goods, accessories, and business-ready bulk purchases.",
    image:
      "https://images.unsplash.com/photo-1746013189357-e17986c93fb2?auto=format&fit=crop&q=80&w=600",
    large: false,
  },
  {
    name: "IDO & OYINGBO MARKET",
    description:
      "Reliable stops for foodstuff, produce, provisions, and practical household market runs.",
    image:
      "https://images.unsplash.com/photo-1734255026082-82fdc81991f0?auto=format&fit=crop&q=80&w=600",
    large: false,
  },
  {
    name: "KETU & MILE 12 MARKET",
    description:
      "High-volume produce, grains, peppers, spices, and everyday food sourcing for homes and businesses.",
    image:
      "https://images.unsplash.com/photo-1722072391426-964abfef1924?auto=format&fit=crop&q=80&w=600",
    large: false,
  },
  {
    name: "ALABA INTERNATIONAL",
    description:
      "Electronics, appliances, equipment, and related accessories sourced with vendor checks and clear updates.",
    image:
      "https://images.unsplash.com/photo-1746013189357-e17986c93fb2?auto=format&fit=crop&q=80&w=600",
    large: false,
  },
  {
    name: "YABA & OSHODI MARKET",
    description:
      "Clothing, fashion items, accessories, fabrics, and everyday essentials across busy Lagos retail hubs.",
    image:
      "https://images.unsplash.com/photo-1768212565426-58b089b6386d?auto=format&fit=crop&q=80&w=600",
    large: false,
  },
];

const DEFAULT_TESTIMONIALS = [
  {
    quote:
      "EKO Markets Procurement saved me time and money. Everything arrived exactly as expected.",
    author: "Adaobi N.",
    role: "Fashion Retailer",
  },
  {
    quote: "Reliable, honest, and very professional.",
    author: "Chukwuemeka O.",
    role: "Business Owner",
  },
  {
    quote: "Perfect solution if you can't show up at the market yourself.",
    author: "Funke A.",
    role: "Diaspora Client",
  },
  {
    quote:
      "They handled my bulk order seamlessly. Highly recommended for SME owners.",
    author: "Ibrahim M.",
    role: "Wholesaler",
  },
];

const DEFAULT_SERVICES = [
  {
    icon: "ShoppingBag",
    title: "Personal Market Shopping",
    description:
      "We shop Lagos markets on your behalf, sourcing exactly what you need without you being physically present.",
  },
  {
    icon: "Package",
    title: "Procurement & Bulk Sourcing",
    description:
      "Designed for retailers, SMEs, and exporters who need reliable sourcing at scale.",
  },
  {
    icon: "Compass",
    title: "Market Guide",
    description:
      "Navigate Lagos markets confidently with expert local guidance and insider knowledge.",
  },
  {
    icon: "Eye",
    title: "Stockpiling and Verification",
    description:
      "For diaspora clients, we receive and stockpile items from different vendors, repackage for logistics, and verify online vendors before you pay.",
  },
];

const SERVICE_ICONS = {
  ShoppingBag,
  Package,
  Compass,
  Eye,
  Shield,
  Search,
  CheckCircle,
  Send,
  MapPin,
};

const getServiceIcon = (iconName) => SERVICE_ICONS[iconName] || ShoppingBag;

const normalizeMarket = (market, index) => {
  const fallback = DEFAULT_MARKETS[index % DEFAULT_MARKETS.length];

  return {
    ...market,
    name: market.name || fallback.name,
    description: market.description || fallback.description,
    image: market.image || fallback.image,
  };
};

// Header Component
const Header = ({ onRequestShopper }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? "bg-white/95 backdrop-blur-md shadow-sm" : "bg-transparent"
      }`}
    >
      <div className="container-custom">
        <div className="flex items-center justify-between h-16 md:h-20">
          <a href="/" className="flex items-center" data-testid="logo-link">
            <BrandLogo />
          </a>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8">
            <a
              href="#markets"
              className="text-stone-600 hover:text-[#073D2D] transition-colors font-medium"
              data-testid="nav-markets"
            >
              Markets
            </a>
            <a
              href="#services"
              className="text-stone-600 hover:text-[#073D2D] transition-colors font-medium"
              data-testid="nav-services"
            >
              Services
            </a>
            <a
              href="#how-it-works"
              className="text-stone-600 hover:text-[#073D2D] transition-colors font-medium"
              data-testid="nav-how-it-works"
            >
              How It Works
            </a>
            <a
              href="/blog"
              className="text-stone-600 hover:text-[#073D2D] transition-colors font-medium"
              data-testid="nav-blog"
            >
              Blog
            </a>
          </nav>

          <div className="hidden md:flex items-center gap-4">
            <a
              href={buildWhatsAppUrl()}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-[#073D2D] font-medium"
              data-testid="header-whatsapp"
            >
              <MessageCircle className="w-5 h-5" />
              Chat
            </a>
            <Button
              onClick={onRequestShopper}
              className="bg-[#073D2D] hover:bg-[#052f23] text-white rounded-full px-6"
              data-testid="header-request-shopper-btn"
            >
              Request a Shopper
            </Button>
          </div>

          {/* Mobile menu button */}
          <button
            className="md:hidden p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-btn"
          >
            {mobileMenuOpen ? (
              <X className="w-6 h-6" />
            ) : (
              <Menu className="w-6 h-6" />
            )}
          </button>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="md:hidden bg-white border-t py-4"
          >
            <nav className="flex flex-col gap-4">
              <a
                href="#markets"
                className="text-stone-600 hover:text-[#073D2D] transition-colors font-medium px-4"
                onClick={() => setMobileMenuOpen(false)}
              >
                Markets
              </a>
              <a
                href="#services"
                className="text-stone-600 hover:text-[#073D2D] transition-colors font-medium px-4"
                onClick={() => setMobileMenuOpen(false)}
              >
                Services
              </a>
              <a
                href="#how-it-works"
                className="text-stone-600 hover:text-[#073D2D] transition-colors font-medium px-4"
                onClick={() => setMobileMenuOpen(false)}
              >
                How It Works
              </a>
              <a
                href="/blog"
                className="text-stone-600 hover:text-[#073D2D] transition-colors font-medium px-4"
              >
                Blog
              </a>
              <div className="px-4 pt-2">
                <Button
                  onClick={() => {
                    setMobileMenuOpen(false);
                    onRequestShopper();
                  }}
                  className="w-full bg-[#073D2D] hover:bg-[#052f23] text-white rounded-full"
                  data-testid="mobile-request-shopper-btn"
                >
                  Request a Shopper
                </Button>
              </div>
            </nav>
          </motion.div>
        )}
      </div>
    </header>
  );
};

// Hero Section
const HeroSection = ({ onRequestShopper }) => {
  return (
    <section className="hero-gradient pt-24 md:pt-32 pb-16 md:pb-24 relative overflow-hidden">
      <div className="container-custom">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="text-left"
          >
            <motion.div
              variants={fadeInUp}
              className="inline-flex items-center gap-2 bg-[#073D2D]/10 text-[#073D2D] px-4 py-2 rounded-full text-sm font-medium mb-6"
            >
              <MapPin className="w-4 h-4" />
              From Lagos Markets to Your Doorstep
            </motion.div>

            <motion.h1
              variants={fadeInUp}
              className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-[#1C1917] leading-tight mb-6"
              data-testid="hero-headline"
            >
              Shop Lagos Markets{" "}
              <span className="gradient-text">Without the Stress</span>
            </motion.h1>

            <motion.p
              variants={fadeInUp}
              className="text-lg text-stone-600 mb-4 max-w-xl"
            >
              EKO Markets Procurement helps individuals, businesses, and diaspora
              clients source products from Lagos markets — transparently,
              efficiently, and without the hassle.
            </motion.p>

            <motion.p
              variants={fadeInUp}
              className="text-base text-stone-500 mb-8 max-w-xl"
            >
              From EKO Market to Trade Fair, Mile 12, Alaba, Yaba, and Oshodi, we handle sourcing, bargaining,
              inspection, and coordination on your behalf.
            </motion.p>

            <motion.div
              variants={fadeInUp}
              className="flex flex-col sm:flex-row gap-4"
            >
              <Button
                onClick={onRequestShopper}
                size="lg"
                className="bg-[#073D2D] hover:bg-[#052f23] text-white rounded-full px-8 py-6 text-lg font-medium"
                data-testid="hero-request-shopper-btn"
              >
                Request a Shopper
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <a
                href={buildWhatsAppUrl(
                  "Hello! I'm interested in your market sourcing services."
                )}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button
                  variant="outline"
                  size="lg"
                  className="border-2 border-[#25D366] text-[#25D366] hover:bg-[#25D366] hover:text-white rounded-full px-8 py-6 text-lg font-medium w-full"
                  data-testid="hero-whatsapp-btn"
                >
                  <MessageCircle className="mr-2 w-5 h-5" />
                  Chat on WhatsApp
                </Button>
              </a>
            </motion.div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="relative"
          >
            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="/hero-personal-shopper.jpeg"
                alt="Personal shopper with colourful fabrics at a Lagos market"
                className="w-full h-[400px] md:h-[500px] object-cover object-[center_40%]"
                width={960}
                height={1280}
                data-testid="hero-image"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
            </div>

            {/* Floating stats card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8 }}
              className="absolute -bottom-6 -left-6 bg-white rounded-xl p-4 shadow-xl"
            >
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-[#e7c139]/10 rounded-full flex items-center justify-center">
                  <Users className="w-6 h-6 text-[#e7c139]" />
                </div>
                <div>
                  <p className="font-accent text-2xl font-bold text-[#073D2D]">
                    500+
                  </p>
                  <p className="text-sm text-stone-500">Happy Clients</p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Social Proof Strip
const SocialProofStrip = () => {
  return (
    <section className="py-8 bg-white border-y border-stone-200">
      <div className="container-custom">
        <p className="text-center text-stone-500 text-sm mb-6">
          Trusted by busy professionals, small business owners, and Nigerians in
          the diaspora
        </p>
        <div className="flex flex-wrap justify-center items-center gap-8 md:gap-16">
          {["SME Owners", "Diaspora Clients", "Retailers", "Event Planners"].map(
            (client, index) => (
              <motion.div
                key={client}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: index * 0.1 }}
                className="flex items-center gap-2 text-stone-400 social-proof-item"
              >
                <CheckCircle className="w-5 h-5" />
                <span className="font-medium">{client}</span>
              </motion.div>
            )
          )}
        </div>
      </div>
    </section>
  );
};

// Why Choose Section
const WhyChooseSection = () => {
  const features = [
    {
      icon: MapPin,
      title: "Deep Market Expertise",
      description:
        "We understand Lagos markets inside out — from real pricing and trusted vendors to market dynamics that help you avoid overpaying.",
    },
    {
      icon: Shield,
      title: "Honest Bargaining",
      description:
        "We negotiate exactly as if you were there yourself, protecting your interests and ensuring fair market prices.",
    },
    {
      icon: Eye,
      title: "Quality Assurance",
      description:
        "Every item is inspected before purchase. You receive confirmations, photos, or videos so you know exactly what you're paying for.",
    },
    {
      icon: Zap,
      title: "Stress-Free Sourcing",
      description:
        "Skip traffic, crowds, and uncertainty. We handle the legwork while you save time and focus on what matters.",
    },
    {
      icon: MessageCircle,
      title: "Real-Time Updates",
      description:
        "Stay informed at every step with clear, timely updates via WhatsApp — no guesswork, no surprises.",
    },
  ];

  return (
    <section id="why-us" className="section-padding bg-[#fffdf3]">
      <div className="container-custom">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="text-center mb-16"
        >
          <motion.h2
            variants={fadeInUp}
            className="font-heading text-3xl md:text-4xl font-bold text-[#1C1917] mb-4"
            data-testid="why-choose-headline"
          >
            Why Choose EKO Markets Procurement
          </motion.h2>
          <motion.p
            variants={fadeInUp}
            className="text-stone-600 max-w-2xl mx-auto"
          >
            We bring transparency, expertise, and reliability to your market
            sourcing needs
          </motion.p>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          {features.map((feature, index) => (
            <motion.div
              key={feature.title}
              variants={fadeInUp}
              className="feature-card group"
              data-testid={`feature-card-${index}`}
            >
              <div className="w-14 h-14 bg-[#073D2D]/10 rounded-xl flex items-center justify-center mb-4 group-hover:bg-[#073D2D] transition-colors">
                <feature.icon className="w-7 h-7 text-[#073D2D] group-hover:text-white transition-colors" />
              </div>
              <h3 className="font-heading text-xl font-semibold text-[#1C1917] mb-2">
                {feature.title}
              </h3>
              <p className="text-stone-600">{feature.description}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// Markets Section (Bento Grid)
const MarketsSection = () => {
  const [markets, setMarkets] = useState([]);

  useEffect(() => {
    const fetchMarkets = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/markets`);
        if (response.data && response.data.length > 0) {
          setMarkets(response.data.map(normalizeMarket));
        } else {
          setMarkets(DEFAULT_MARKETS);
        }
      } catch (error) {
        console.log("Using default markets");
        setMarkets(DEFAULT_MARKETS);
      }
    };
    fetchMarkets();
  }, []);

  const displayMarkets = markets.length > 0 ? markets : DEFAULT_MARKETS;

  return (
    <section id="markets" className="section-padding bg-[#fff7d6]">
      <div className="container-custom">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="text-center mb-16"
        >
          <motion.h2
            variants={fadeInUp}
            className="font-heading text-3xl md:text-4xl font-bold text-[#1C1917] mb-4"
            data-testid="markets-headline"
          >
            Markets We Cover
          </motion.h2>
          <motion.p
            variants={fadeInUp}
            className="text-stone-600 max-w-2xl mx-auto"
          >
            We operate across major Lagos markets, sourcing directly from
            trusted vendors to ensure quality, fair pricing, and reliable
            delivery.
          </motion.p>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="bento-grid"
        >
          {displayMarkets.map((market, index) => (
            <motion.div
              key={market.name}
              variants={fadeInUp}
              className={`market-card ${market.large ? "large" : ""}`}
              data-testid={`market-card-${index}`}
            >
              <img
                src={market.image}
                alt={market.name}
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="market-card-overlay" />
              <div className="market-card-content absolute bottom-0 left-0 right-0 p-6 text-white">
                <h3 className="font-heading text-xl md:text-2xl font-bold mb-2">
                  {market.name}
                </h3>
                <p className="text-white/90 text-sm md:text-base">
                  {market.description}
                </p>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// How It Works Section
const HowItWorksSection = () => {
  const steps = [
    {
      number: "01",
      title: "Send Your Request",
      description:
        "Share your shopping list, specifications, budget range, and timeline.",
      icon: Send,
    },
    {
      number: "02",
      title: "Price & Service Confirmation",
      description:
        "We confirm product availability, pricing, and service fees before proceeding.",
      icon: CheckCircle,
    },
    {
      number: "03",
      title: "We Source, Inspect & Bargain",
      description:
        "We purchase from trusted vendors, negotiate fairly, and inspect items before payment.",
      icon: Search,
    },
    {
      number: "04",
      title: "Delivery & Reporting",
      description:
        "Items delivered within Lagos or coordinated for onward shipping, with updates throughout.",
      icon: Package,
    },
  ];

  return (
    <section id="how-it-works" className="section-padding bg-[#fffdf3]">
      <div className="container-custom">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="text-center mb-16"
        >
          <motion.h2
            variants={fadeInUp}
            className="font-heading text-3xl md:text-4xl font-bold text-[#1C1917] mb-4"
            data-testid="how-it-works-headline"
          >
            How It Works
          </motion.h2>
          <motion.p
            variants={fadeInUp}
            className="text-stone-600 max-w-2xl mx-auto"
          >
            Four simple steps to stress-free market sourcing
          </motion.p>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="grid md:grid-cols-2 lg:grid-cols-4 gap-8"
        >
          {steps.map((step, index) => (
            <motion.div
              key={step.number}
              variants={fadeInUp}
              className="relative text-center"
              data-testid={`step-card-${index}`}
            >
              <div className="relative inline-block mb-6">
                <div className="w-20 h-20 bg-[#073D2D] rounded-2xl flex items-center justify-center mx-auto">
                  <step.icon className="w-8 h-8 text-white" />
                </div>
                <span className="absolute -top-2 -right-2 w-8 h-8 bg-[#e7c139] rounded-full flex items-center justify-center font-accent text-sm font-bold text-black">
                  {step.number}
                </span>
              </div>
              <h3 className="font-heading text-xl font-semibold text-[#1C1917] mb-3">
                {step.title}
              </h3>
              <p className="text-stone-600">{step.description}</p>

              {index < steps.length - 1 && (
                <div className="hidden lg:block absolute top-10 left-[60%] w-[80%]">
                  <ChevronRight className="w-6 h-6 text-[#e7c139]" />
                </div>
              )}
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// Services Section
const ServicesSection = () => {
  const [services, setServices] = useState(DEFAULT_SERVICES);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/services`);
        if (response.data && response.data.length > 0) {
          setServices(response.data);
        } else {
          setServices(DEFAULT_SERVICES);
        }
      } catch (error) {
        console.log("Using default services");
        setServices(DEFAULT_SERVICES);
      }
    };

    fetchServices();
  }, []);

  return (
    <section id="services" className="section-padding bg-[#fff7d6]">
      <div className="container-custom">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="text-center mb-16"
        >
          <motion.h2
            variants={fadeInUp}
            className="font-heading text-3xl md:text-4xl font-bold text-[#1C1917] mb-4"
            data-testid="services-headline"
          >
            Our Services
          </motion.h2>
          <motion.p
            variants={fadeInUp}
            className="text-stone-600 max-w-2xl mx-auto"
          >
            Comprehensive market sourcing solutions tailored to your needs
          </motion.p>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="grid md:grid-cols-2 gap-6"
        >
          {services.map((service, index) => {
            const ServiceIcon = getServiceIcon(service.icon);

            return (
              <motion.div
                key={service.id || service.title}
                variants={fadeInUp}
                className="service-card flex gap-4"
                data-testid={`service-card-${index}`}
              >
                <div className="w-14 h-14 bg-[#073D2D] rounded-xl flex items-center justify-center flex-shrink-0">
                  <ServiceIcon className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h3 className="font-heading text-xl font-semibold text-[#1C1917] mb-2">
                    {service.title}
                  </h3>
                  <p className="text-stone-600">{service.description}</p>
                </div>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
};

// Testimonials Section
const TestimonialsSection = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [testimonialsApi, setTestimonialsApi] = useState(null);
  const [activeSlide, setActiveSlide] = useState(0);
  const [isAutoPlayEnabled, setIsAutoPlayEnabled] = useState(true);

  useEffect(() => {
    const fetchTestimonials = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/testimonials`);
        if (response.data && response.data.length > 0) {
          setTestimonials(response.data);
        } else {
          setTestimonials(DEFAULT_TESTIMONIALS);
        }
      } catch (error) {
        console.log("Using default testimonials");
        setTestimonials(DEFAULT_TESTIMONIALS);
      }
    };
    fetchTestimonials();
  }, []);

  const displayTestimonials = testimonials.length > 0 ? testimonials : DEFAULT_TESTIMONIALS;

  useEffect(() => {
    if (!testimonialsApi) return;

    const onSelect = () => setActiveSlide(testimonialsApi.selectedScrollSnap());
    onSelect();
    testimonialsApi.on("select", onSelect);
    testimonialsApi.on("reInit", onSelect);

    return () => {
      testimonialsApi.off("select", onSelect);
      testimonialsApi.off("reInit", onSelect);
    };
  }, [testimonialsApi]);

  useEffect(() => {
    if (!testimonialsApi || !isAutoPlayEnabled || displayTestimonials.length <= 1) return;
    const intervalId = setInterval(() => {
      testimonialsApi.scrollNext();
    }, 5000);
    return () => clearInterval(intervalId);
  }, [testimonialsApi, isAutoPlayEnabled, displayTestimonials.length]);

  return (
    <section className="section-padding bg-[#fffdf3] overflow-hidden">
      <div className="container-custom">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="text-center mb-16"
        >
          <motion.h2
            variants={fadeInUp}
            className="font-heading text-3xl md:text-4xl font-bold text-[#1C1917] mb-4"
            data-testid="testimonials-headline"
          >
            What Our Clients Say
          </motion.h2>
        </motion.div>

        <div className="max-w-4xl mx-auto">
          <Carousel
            setApi={setTestimonialsApi}
            opts={{ align: "start", loop: true }}
            className="w-full"
            onMouseEnter={() => setIsAutoPlayEnabled(false)}
            onMouseLeave={() => setIsAutoPlayEnabled(true)}
          >
            <CarouselContent>
              {displayTestimonials.map((testimonial, index) => (
                <CarouselItem
                  key={`${testimonial.author}-${index}`}
                  className="basis-full md:basis-1/2 lg:basis-1/3"
                >
                  <div
                    className="testimonial-card border border-stone-200 p-6 md:p-7 h-full"
                    data-testid={`testimonial-card-${index}`}
                  >
                    <div className="flex gap-1 mb-5">
                      {[...Array(5)].map((_, i) => (
                        <Star
                          key={i}
                          className="w-5 h-5 fill-[#e7c139] text-[#e7c139]"
                        />
                      ))}
                    </div>
                    <p className="text-stone-700 mb-6 text-lg leading-relaxed italic">
                      "{testimonial.quote}"
                    </p>
                    <div>
                      <p className="font-semibold text-[#1C1917] text-lg">
                        {testimonial.author}
                      </p>
                      <p className="text-stone-500">{testimonial.role}</p>
                    </div>
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
          </Carousel>
          <div className="flex items-center justify-center gap-3 mt-8">
            <Button
              type="button"
              variant="outline"
              className="rounded-full border-stone-300"
              onClick={() => testimonialsApi?.scrollPrev()}
              aria-label="Previous testimonial"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <div className="flex items-center gap-2">
              {displayTestimonials.map((_, index) => (
                <button
                  key={`testimonial-dot-${index}`}
                  type="button"
                  onClick={() => testimonialsApi?.scrollTo(index)}
                  className={`h-2.5 rounded-full transition-all ${
                    activeSlide === index
                      ? "w-6 bg-[#073D2D]"
                      : "w-2.5 bg-stone-300 hover:bg-stone-400"
                  }`}
                  aria-label={`Go to testimonial ${index + 1}`}
                />
              ))}
            </div>
            <Button
              type="button"
              variant="outline"
              className="rounded-full border-stone-300"
              onClick={() => testimonialsApi?.scrollNext()}
              aria-label="Next testimonial"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
            <Button
              type="button"
              variant="outline"
              className="rounded-full border-stone-300"
              onClick={() => setIsAutoPlayEnabled((prev) => !prev)}
              aria-label={isAutoPlayEnabled ? "Pause testimonial autoplay" : "Resume testimonial autoplay"}
            >
              {isAutoPlayEnabled ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

// Blog Preview Section
const BlogPreviewSection = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/blog/posts?per_page=3`);
        setPosts(response.data.posts || []);
      } catch (error) {
        console.error("Error fetching blog posts:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchPosts();
  }, []);

  // Placeholder posts if API returns empty
  const placeholderPosts = [
    {
      id: 1,
      title: "Guide to Shopping at Balogun Market",
      excerpt:
        "Discover the best tips for navigating Lagos' largest fabric and fashion market...",
      slug: "guide-balogun-market",
      featured_image_url: "https://images.unsplash.com/photo-1768212565426-58b089b6386d?auto=format&fit=crop&q=80&w=400",
    },
    {
      id: 2,
      title: "How to Source Quality Fabrics in Lagos",
      excerpt:
        "Learn how to identify authentic materials and get the best deals...",
      slug: "source-quality-fabrics",
      featured_image_url: "https://images.unsplash.com/photo-1746013189357-e17986c93fb2?auto=format&fit=crop&q=80&w=400",
    },
    {
      id: 3,
      title: "Bulk Buying Tips for Business Owners",
      excerpt:
        "Essential strategies for successful wholesale purchasing in Lagos markets...",
      slug: "bulk-buying-tips",
      featured_image_url: "https://images.unsplash.com/photo-1722072391426-964abfef1924?auto=format&fit=crop&q=80&w=400",
    },
  ];

  const displayPosts = posts.length > 0 ? posts : placeholderPosts;

  return (
    <section className="section-padding bg-[#fff7d6]">
      <div className="container-custom">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-12"
        >
          <div>
            <motion.h2
              variants={fadeInUp}
              className="font-heading text-3xl md:text-4xl font-bold text-[#1C1917] mb-2"
              data-testid="blog-headline"
            >
              From Our Blog
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-stone-600">
              Tips, guides, and market insights
            </motion.p>
          </div>
          <motion.a
            variants={fadeInUp}
            href="/blog"
            className="flex items-center gap-2 text-[#073D2D] font-medium hover:gap-3 transition-all"
            data-testid="view-all-posts-link"
          >
            View all posts
            <ArrowRight className="w-4 h-4" />
          </motion.a>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="grid md:grid-cols-3 gap-6"
        >
          {loading
            ? [...Array(3)].map((_, index) => (
                <div
                  key={index}
                  className="blog-card animate-pulse"
                >
                  <div className="h-48 bg-stone-200" />
                  <div className="p-6">
                    <div className="h-4 bg-stone-200 rounded mb-2" />
                    <div className="h-4 bg-stone-200 rounded w-2/3" />
                  </div>
                </div>
              ))
            : displayPosts.map((post, index) => (
                <motion.a
                  key={post.id}
                  href={posts.length > 0 ? `/blog/${post.slug}` : "#"}
                  variants={fadeInUp}
                  className="blog-card group"
                  data-testid={`blog-card-${index}`}
                >
                  <div className="h-48 overflow-hidden">
                    <img
                      src={
                        post.featured_image_url ||
                        "https://images.unsplash.com/photo-1768212565426-58b089b6386d?auto=format&fit=crop&q=80&w=400"
                      }
                      alt={post.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="font-heading text-lg font-semibold text-[#1C1917] mb-2 group-hover:text-[#073D2D] transition-colors">
                      {post.title}
                    </h3>
                    <p
                      className="text-stone-600 text-sm line-clamp-2"
                      dangerouslySetInnerHTML={{
                        __html: sanitizeHtml(post.excerpt),
                      }}
                    />
                  </div>
                </motion.a>
              ))}
        </motion.div>
      </div>
    </section>
  );
};

// Newsletter Section
const NewsletterSection = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const useWordPressNewsletterForm = Boolean(MAILPOET_NEWSLETTER_FORM_URL);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;

    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/newsletter/subscribe`, { email });
      toast.success("Successfully subscribed to newsletter!");
      setEmail("");
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error("Email already subscribed");
      } else {
        toast.error("Failed to subscribe. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="newsletter-section py-16 md:py-20">
      <div className="container-custom">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="text-center max-w-2xl mx-auto"
        >
          <motion.h2
            variants={fadeInUp}
            className="font-heading text-2xl md:text-3xl font-bold text-white mb-4"
            data-testid="newsletter-headline"
          >
            Stay Updated with Market Insights
          </motion.h2>
          <motion.p
            variants={fadeInUp}
            className="text-white/80 mb-8"
          >
            Get tips, market updates, and exclusive offers delivered to your
            inbox
          </motion.p>
          <motion.form
            variants={fadeInUp}
            onSubmit={handleSubmit}
            className="flex flex-col sm:flex-row gap-4"
            data-testid="newsletter-form"
          >
            {useWordPressNewsletterForm ? (
              <div className="w-full rounded-2xl overflow-hidden border border-white/20 bg-white">
                <iframe
                  src={MAILPOET_NEWSLETTER_FORM_URL}
                  title="Newsletter Subscription Form"
                  className="w-full h-[220px] md:h-[180px]"
                  loading="lazy"
                />
              </div>
            ) : (
              <>
                <Input
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="flex-1 bg-white/10 border-white/20 text-white placeholder:text-white/60 rounded-full px-6 py-6"
                  data-testid="newsletter-email-input"
                />
                <Button
                  type="submit"
                  disabled={loading}
                  className="bg-[#e7c139] hover:bg-[#c5a32f] text-black rounded-full px-8 py-6 font-medium"
                  data-testid="newsletter-submit-btn"
                >
                  {loading ? "Subscribing..." : "Subscribe"}
                </Button>
              </>
            )}
          </motion.form>
        </motion.div>
      </div>
    </section>
  );
};

// Final CTA Section
const FinalCTASection = ({ onRequestShopper }) => {
  return (
    <section className="section-padding bg-[#fffdf3]">
      <div className="container-custom">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="text-center max-w-3xl mx-auto"
        >
          <motion.h2
            variants={fadeInUp}
            className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-[#1C1917] mb-6"
            data-testid="final-cta-headline"
          >
            Ready to shop Lagos markets the smart way?
          </motion.h2>
          <motion.p
            variants={fadeInUp}
            className="text-lg text-stone-600 mb-8"
          >
            Let EKO Markets Procurement handle sourcing, bargaining, and inspections —
            while you focus on what matters most.
          </motion.p>
          <motion.div
            variants={fadeInUp}
            className="flex flex-col sm:flex-row gap-4 justify-center"
          >
            <Button
              onClick={onRequestShopper}
              size="lg"
              className="bg-[#073D2D] hover:bg-[#052f23] text-white rounded-full px-8 py-6 text-lg font-medium"
              data-testid="final-cta-request-btn"
            >
              Request a Shopper
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
            <a
              href={buildWhatsAppUrl(
                "Hello! I'm interested in your market sourcing services."
              )}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button
                variant="outline"
                size="lg"
                className="border-2 border-[#25D366] text-[#25D366] hover:bg-[#25D366] hover:text-white rounded-full px-8 py-6 text-lg font-medium w-full"
                data-testid="final-cta-whatsapp-btn"
              >
                <MessageCircle className="mr-2 w-5 h-5" />
                Chat on WhatsApp
              </Button>
            </a>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  return (
    <footer className="bg-[#073D2D] text-white py-16">
      <div className="container-custom">
        <div className="grid md:grid-cols-4 gap-12">
          <div className="md:col-span-2">
            <div className="inline-flex mb-4">
              <BrandLogo variant="footer" />
            </div>
            <p className="text-stone-400 mb-6 max-w-md">
              Your trusted partner for sourcing products from Lagos markets.
              Transparent, efficient, and stress-free.
            </p>
            <div className="flex gap-4">
              <a
                href={buildWhatsAppUrl()}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-[#25D366] transition-colors"
                data-testid="footer-whatsapp"
                aria-label="Chat with us on WhatsApp"
                title="Chat with us on WhatsApp"
              >
                <WhatsAppIcon className="w-5 h-5" />
              </a>
              <a
                href="https://www.instagram.com/balogunpersonalshopper"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-[#e7c139] hover:text-[#073D2D] transition-colors"
                data-testid="footer-instagram"
                aria-label="Follow us on Instagram"
                title="Follow us on Instagram"
              >
                <Instagram className="w-5 h-5" aria-hidden="true" />
              </a>
              <a
                href="mailto:hello@ekomarketspro.com"
                className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-[#073D2D] transition-colors"
                data-testid="footer-email"
                aria-label="Email us"
              >
                <Mail className="w-5 h-5" />
              </a>
            </div>
          </div>

          <div>
            <h4 className="font-heading font-semibold mb-4">Quick Links</h4>
            <ul className="space-y-2">
              <li>
                <a
                  href="#markets"
                  className="text-stone-400 hover:text-white transition-colors"
                >
                  Markets
                </a>
              </li>
              <li>
                <a
                  href="#services"
                  className="text-stone-400 hover:text-white transition-colors"
                >
                  Services
                </a>
              </li>
              <li>
                <a
                  href="#how-it-works"
                  className="text-stone-400 hover:text-white transition-colors"
                >
                  How It Works
                </a>
              </li>
              <li>
                <a
                  href="/blog"
                  className="text-stone-400 hover:text-white transition-colors"
                >
                  Blog
                </a>
              </li>
              <li>
                <a
                  href="/reviews"
                  className="text-stone-400 hover:text-white transition-colors"
                >
                  Leave a Review
                </a>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="font-heading font-semibold mb-4">Contact</h4>
            <ul className="space-y-3">
              <li className="flex items-center gap-2 text-stone-400">
                <Phone className="w-4 h-4" />
                <span>+234 818 045 8781</span>
              </li>
              <li className="flex items-center gap-2 text-stone-400">
                <Mail className="w-4 h-4" />
                <span>hello@ekomarketspro.com</span>
              </li>
              <li className="flex items-center gap-2 text-stone-400">
                <Clock className="w-4 h-4" />
                <span>Mon - Sat: 8am - 6pm</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-white/10 mt-12 pt-8 text-center text-stone-500">
          <p>
            &copy; {new Date().getFullYear()} EKO Markets Procurement. All rights
            reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};

// WhatsApp Floating Button
const WhatsAppFloat = () => {
  return (
    <a
      href={buildWhatsAppUrl(
        "Hello! I'm interested in your market sourcing services."
      )}
      target="_blank"
      rel="noopener noreferrer"
      className="whatsapp-float animate-pulse-glow"
      data-testid="whatsapp-float-btn"
      aria-label="Chat with us on WhatsApp"
    >
      <WhatsAppIcon className="w-7 h-7 text-white" />
    </a>
  );
};

// Request Shopper Modal
const RequestShopperModal = ({ open, onOpenChange }) => {
  const [loading, setLoading] = useState(false);
  const useWordPressShopperForm = Boolean(ELEMENTOR_SHOPPER_FORM_URL);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    market: "",
    items: "",
    budget: "",
    timeline: "",
    additional_notes: "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (value) => {
    setFormData((prev) => ({ ...prev, market: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API_URL}/api/shopper-request`, formData);
      toast.success("Request submitted successfully! We'll contact you soon.");
      onOpenChange(false);
      setFormData({
        name: "",
        email: "",
        phone: "",
        market: "",
        items: "",
        budget: "",
        timeline: "",
        additional_notes: "",
      });
    } catch (error) {
      toast.error("Failed to submit request. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={`max-h-[90vh] overflow-y-auto ${useWordPressShopperForm ? "sm:max-w-[760px]" : "sm:max-w-[500px]"}`}>
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">
            Request a Shopper
          </DialogTitle>
        </DialogHeader>
        {useWordPressShopperForm ? (
          <div className="mt-4 rounded-xl overflow-hidden border border-stone-200">
            <iframe
              src={ELEMENTOR_SHOPPER_FORM_URL}
              title="Request a Shopper Form"
              className="w-full h-[70vh]"
              loading="lazy"
            />
          </div>
        ) : (
        <form onSubmit={handleSubmit} className="space-y-4 mt-4" data-testid="shopper-request-form">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="name">Full Name *</Label>
              <Input
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                placeholder="Your name"
                data-testid="shopper-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone">Phone Number *</Label>
              <Input
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                required
                placeholder="+234..."
                data-testid="shopper-phone-input"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">Email Address *</Label>
            <Input
              id="email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              required
              placeholder="your@email.com"
              data-testid="shopper-email-input"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="market">Preferred Market *</Label>
            <Select onValueChange={handleSelectChange} value={formData.market}>
              <SelectTrigger data-testid="shopper-market-select">
                <SelectValue placeholder="Select a market" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="eko-market">EKO Market</SelectItem>
                <SelectItem value="trade-fair">TRADE FAIR Market</SelectItem>
                <SelectItem value="ido-oyingbo">IDO & OYINGBO MARKET</SelectItem>
                <SelectItem value="ketu-mile-12">KETU & MILE 12 MARKET</SelectItem>
                <SelectItem value="alaba">ALABA INTERNATIONAL</SelectItem>
                <SelectItem value="yaba-oshodi">YABA & OSHODI MARKET</SelectItem>
                <SelectItem value="other">Other / Not Sure</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="items">What do you need? *</Label>
            <Textarea
              id="items"
              name="items"
              value={formData.items}
              onChange={handleChange}
              required
              placeholder="Describe the items you need (e.g., 5 yards of lace fabric, specific colors...)"
              rows={3}
              data-testid="shopper-items-input"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="budget">Budget Range</Label>
              <Input
                id="budget"
                name="budget"
                value={formData.budget}
                onChange={handleChange}
                placeholder="e.g., ₦50,000 - ₦100,000"
                data-testid="shopper-budget-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="timeline">Timeline</Label>
              <Input
                id="timeline"
                name="timeline"
                value={formData.timeline}
                onChange={handleChange}
                placeholder="e.g., Within 1 week"
                data-testid="shopper-timeline-input"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="additional_notes">Additional Notes</Label>
            <Textarea
              id="additional_notes"
              name="additional_notes"
              value={formData.additional_notes}
              onChange={handleChange}
              placeholder="Any other details we should know..."
              rows={2}
              data-testid="shopper-notes-input"
            />
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-[#073D2D] hover:bg-[#052f23] text-white rounded-full py-6"
            data-testid="shopper-submit-btn"
          >
            {loading ? "Submitting..." : "Submit Request"}
          </Button>
        </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

// Main Landing Page Component
export default function LandingPage() {
  const [modalOpen, setModalOpen] = useState(false);

  const handleRequestShopper = () => {
    setModalOpen(true);
  };

  return (
    <div className="min-h-screen" data-testid="landing-page">
      <Header onRequestShopper={handleRequestShopper} />
      <HeroSection onRequestShopper={handleRequestShopper} />
      <SocialProofStrip />
      <WhyChooseSection />
      <MarketsSection />
      <HowItWorksSection />
      <ServicesSection />
      <TestimonialsSection />
      <BlogPreviewSection />
      <NewsletterSection />
      <FinalCTASection onRequestShopper={handleRequestShopper} />
      <Footer />
      <WhatsAppFloat />
      <RequestShopperModal open={modalOpen} onOpenChange={setModalOpen} />
    </div>
  );
}
